import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import apiClient from '../api/client';

function Analytics() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userExamType, setUserExamType] = useState('selective');
  const [stats, setStats] = useState({
    totalTests: 0,
    averageScore: 0,
    totalTimeSpent: 0,
    totalQuestions: 0,
    writingActivities: 0,
    recentTests: [],
    currentStreak: 0
  });

  useEffect(() => {
    fetchAnalytics();
    // Get user exam type
    const fetchExamType = async () => {
      try {
        const { fetchUserAttributes } = await import('aws-amplify/auth');
        const attributes = await fetchUserAttributes();
        setUserExamType(attributes['custom:exam_type'] || 'selective');
      } catch (err) {
        console.log('Could not fetch exam type');
      }
    };
    fetchExamType();
  }, []);

  const fetchAnalytics = async () => {
    try {
      // Fetch test results
      const resultsResponse = await apiClient.get('/user/results');
      let recentTests = resultsResponse.data.recentTests || [];
      
      // Fetch writing activities
      let writingActivities = 0;
      try {
        const writingsResponse = await apiClient.get('/writings');
        writingActivities = writingsResponse.data.writings?.length || 0;
      } catch (err) {
        console.log('Could not fetch writings:', err);
      }

      // Calculate total questions from all tests
      const totalQuestions = recentTests.reduce((sum, test) => 
        sum + (test.totalQuestions || 0), 0
      );

      // Calculate streak from all tests
      const streak = calculateStreak(recentTests);

      setStats({
        totalTests: recentTests.length,
        averageScore: recentTests.length > 0 
          ? Math.round(recentTests.reduce((sum, t) => sum + t.score, 0) / recentTests.length)
          : 0,
        totalTimeSpent: recentTests.reduce((sum, t) => sum + (t.timeSpent || 0), 0),
        totalQuestions,
        writingActivities,
        recentTests,
        currentStreak: streak
      });
    } catch (err) {
      console.error('Error fetching analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateStreak = (tests) => {
    if (tests.length === 0) return 0;
    
    // Sort tests by date (most recent first)
    const sortedTests = [...tests].sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB - dateA;
    });
    
    let streak = 0;
    let currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    
    for (let test of sortedTests) {
      const testDate = new Date(test.date);
      testDate.setHours(0, 0, 0, 0);
      
      const daysDiff = Math.floor((currentDate - testDate) / (1000 * 60 * 60 * 24));
      
      if (daysDiff === streak) {
        streak++;
      } else if (daysDiff > streak) {
        break;
      }
    }
    
    return streak;
  };

  const formatTime = (seconds) => {
    if (!seconds || seconds === 0) return '0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const getSubjectStats = () => {
    const subjects = {
      'Mathematical Reasoning': { total: 0, correct: 0, tests: 0, color: '#3b82f6' },
      'Reading': { total: 0, correct: 0, tests: 0, color: '#10b981' },
      'Thinking Skills': { total: 0, correct: 0, tests: 0, color: '#f59e0b' },
      ...(userExamType !== 'oc' ? { 'Writing': { total: 0, correct: 0, tests: 0, color: '#ef4444' } } : {})
    };

    stats.recentTests.forEach(test => {
      if (subjects[test.topic]) {
        subjects[test.topic].total += test.totalQuestions || 0;
        subjects[test.topic].correct += test.correctAnswers || 0;
        subjects[test.topic].tests += 1;
      }
    });

    return Object.entries(subjects).map(([name, data]) => ({
      name,
      accuracy: data.total > 0 ? Math.round((data.correct / data.total) * 100) : 0,
      questionsAnswered: data.total,
      testsCompleted: data.tests,
      color: data.color
    }));
  };

  const getTopicBreakdown = () => {
    const topics = {};

    // Analyze performance at the question level
    stats.recentTests.forEach(test => {
      if (test.questions && Array.isArray(test.questions)) {
        test.questions.forEach(question => {
          const topicName = question.topicName || test.topicName || test.title || 'General Practice';
          
          if (!topics[topicName]) {
            topics[topicName] = { total: 0, correct: 0, tests: new Set(), subject: test.topic };
          }
          
          topics[topicName].total += 1;
          if (question.isCorrect) {
            topics[topicName].correct += 1;
          }
          topics[topicName].tests.add(test.resultId || test.examId);
        });
      } else {
        // Fallback for tests without question-level data
        const topicName = test.topicName || test.title || 'General Practice';
        if (!topics[topicName]) {
          topics[topicName] = { total: 0, correct: 0, tests: new Set(), subject: test.topic };
        }
        topics[topicName].total += test.totalQuestions || 0;
        topics[topicName].correct += test.correctAnswers || 0;
        topics[topicName].tests.add(test.resultId || test.examId);
      }
    });

    return Object.entries(topics)
      .map(([name, data]) => ({
        name,
        accuracy: data.total > 0 ? Math.round((data.correct / data.total) * 100) : 0,
        questions: data.total,
        tests: data.tests.size,
        subject: data.subject || 'Reading' // Default to Reading if subject not found
      }))
      .filter(topic => topic.questions > 0) // Only show topics with questions
      .sort((a, b) => b.accuracy - a.accuracy);
  };

  const getCategoryStats = () => {
    const categories = {
      'MockTests': { total: 0, correct: 0, tests: 0 },
      'TopicBased': { total: 0, correct: 0, tests: 0 }
    };

    stats.recentTests.forEach(test => {
      const category = test.category || 'MockTests';
      if (categories[category]) {
        categories[category].total += test.totalQuestions || 0;
        categories[category].correct += test.correctAnswers || 0;
        categories[category].tests += 1;
      }
    });

    return [
      {
        name: 'Mock Tests',
        accuracy: categories.MockTests.total > 0 
          ? Math.round((categories.MockTests.correct / categories.MockTests.total) * 100) 
          : 0,
        tests: categories.MockTests.tests,
        color: '#8b5cf6'
      },
      {
        name: 'Topic Tests',
        accuracy: categories.TopicBased.total > 0 
          ? Math.round((categories.TopicBased.correct / categories.TopicBased.total) * 100) 
          : 0,
        tests: categories.TopicBased.tests,
        color: '#06b6d4'
      }
    ];
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#10b981';
    if (score >= 60) return '#f59e0b';
    return '#ef4444';
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>📊</div>
        <p style={{ fontSize: '18px', color: '#666' }}>Loading your analytics...</p>
      </div>
    );
  }

  const subjectStats = getSubjectStats();
  const topicBreakdown = getTopicBreakdown();
  const categoryStats = getCategoryStats();
  const topTopics = topicBreakdown.slice(0, 5);
  // For weak topics, filter out perfect scores and show lowest performers
  const weakTopics = topicBreakdown
    .filter(topic => topic.accuracy < 80) // Only show topics below 80%
    .slice(-5)
    .reverse();

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', padding: '30px 20px' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        
        {/* Header */}
        <div style={{ marginBottom: '30px' }}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              background: 'white',
              border: '1px solid #e5e7eb',
              padding: '10px 20px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            ← Back to Dashboard
          </button>
          <h1 style={{ fontSize: '32px', fontWeight: 'bold', color: '#1f2937', marginBottom: '8px' }}>
            📊 Performance Analytics
          </h1>
          <p style={{ fontSize: '16px', color: '#6b7280' }}>
            Track your progress and identify areas for improvement
          </p>
        </div>

        {/* Overview Cards */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '20px',
          marginBottom: '30px'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            padding: '25px',
            borderRadius: '16px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '14px', opacity: '0.9', marginBottom: '8px' }}>Total Tests</div>
            <div style={{ fontSize: '36px', fontWeight: 'bold' }}>{stats.totalTests}</div>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
            padding: '25px',
            borderRadius: '16px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '14px', opacity: '0.9', marginBottom: '8px' }}>Average Score</div>
            <div style={{ fontSize: '36px', fontWeight: 'bold' }}>{stats.averageScore}%</div>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            color: 'white',
            padding: '25px',
            borderRadius: '16px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '14px', opacity: '0.9', marginBottom: '8px' }}>Study Time</div>
            <div style={{ fontSize: '36px', fontWeight: 'bold' }}>{formatTime(stats.totalTimeSpent)}</div>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
            color: 'white',
            padding: '25px',
            borderRadius: '16px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '14px', opacity: '0.9', marginBottom: '8px' }}>Current Streak</div>
            <div style={{ fontSize: '36px', fontWeight: 'bold' }}>{stats.currentStreak} days</div>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            color: 'white',
            padding: '25px',
            borderRadius: '16px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '14px', opacity: '0.9', marginBottom: '8px' }}>Questions Answered</div>
            <div style={{ fontSize: '36px', fontWeight: 'bold' }}>{stats.totalQuestions}</div>
          </div>

          {userExamType !== 'oc' && (
          <div style={{
            background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            color: '#1f2937',
            padding: '25px',
            borderRadius: '16px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '14px', opacity: '0.8', marginBottom: '8px' }}>Writing Activities</div>
            <div style={{ fontSize: '36px', fontWeight: 'bold' }}>{stats.writingActivities}</div>
          </div>
          )}
        </div>

        {/* Subject Performance */}
        <div style={{ 
          background: 'white', 
          padding: '30px', 
          borderRadius: '16px', 
          marginBottom: '30px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937', marginBottom: '20px' }}>
            📚 Subject Performance
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
            {subjectStats.map((subject, index) => (
              <div key={index} style={{
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                padding: '20px',
                background: '#f9fafb'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  marginBottom: '15px'
                }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                    {subject.name}
                  </h3>
                  <div style={{
                    fontSize: '24px',
                    fontWeight: 'bold',
                    color: subject.color
                  }}>
                    {subject.accuracy}%
                  </div>
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
                  📝 {subject.testsCompleted} tests completed
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  ❓ {subject.questionsAnswered} questions answered
                </div>
                <div style={{
                  marginTop: '15px',
                  height: '8px',
                  background: '#e5e7eb',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    width: `${subject.accuracy}%`,
                    background: subject.color,
                    transition: 'width 0.3s'
                  }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Category Comparison */}
        <div style={{ 
          background: 'white', 
          padding: '30px', 
          borderRadius: '16px', 
          marginBottom: '30px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937', marginBottom: '20px' }}>
            🎯 Test Category Performance
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            {categoryStats.map((category, index) => (
              <div key={index} style={{
                border: `2px solid ${category.color}`,
                borderRadius: '12px',
                padding: '25px',
                background: `${category.color}10`
              }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '15px' }}>
                  {category.name}
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '15px' }}>
                  <div>
                    <div style={{ fontSize: '36px', fontWeight: 'bold', color: category.color }}>
                      {category.accuracy}%
                    </div>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>Accuracy</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#1f2937' }}>
                      {category.tests}
                    </div>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>Tests</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top & Weak Topics */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px', marginBottom: '30px' }}>
          {/* Top Topics */}
          <div style={{ 
            background: 'white', 
            padding: '30px', 
            borderRadius: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', marginBottom: '20px' }}>
              🌟 Top Performing Topics
            </h2>
            {topTopics.length > 0 ? (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {topTopics.map((topic, index) => (
                    <Link 
                      key={index}
                      to={`/topic-tests?subject=${encodeURIComponent(topic.subject)}`}
                      style={{ textDecoration: 'none' }}
                    >
                      <div style={{
                        padding: '15px',
                        background: '#f0fdf4',
                        border: '1px solid #86efac',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateX(5px)';
                        e.currentTarget.style.boxShadow = '0 4px 6px rgba(16, 185, 129, 0.1)';
                        e.currentTarget.style.borderColor = '#10b981';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateX(0)';
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.borderColor = '#86efac';
                      }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', marginBottom: '4px' }}>
                              {topic.name}
                            </div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>
                              {topic.questions} questions • {topic.tests} tests
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#10b981' }}>
                              {topic.accuracy}%
                            </div>
                            <div style={{ fontSize: '18px', color: '#9ca3af' }}>→</div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </>
            ) : (
              <p style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>
                Complete more tests to see your top topics
              </p>
            )}
          </div>

          {/* Weak Topics */}
          <div style={{ 
            background: 'white', 
            padding: '30px', 
            borderRadius: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', marginBottom: '20px' }}>
              📈 Topics to Improve
            </h2>
            {weakTopics.length > 0 ? (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {weakTopics.slice(0, 10).map((topic, index) => (
                    <Link 
                      key={index}
                      to={`/topic-tests?subject=${encodeURIComponent(topic.subject)}`}
                      style={{ textDecoration: 'none' }}
                    >
                      <div style={{
                        padding: '15px',
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateX(5px)';
                        e.currentTarget.style.boxShadow = '0 4px 6px rgba(239, 68, 68, 0.1)';
                        e.currentTarget.style.borderColor = '#ef4444';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateX(0)';
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.borderColor = '#fecaca';
                      }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', marginBottom: '4px' }}>
                              {topic.name}
                            </div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>
                              {topic.questions} questions • {topic.tests} tests
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ fontSize: '20px', fontWeight: 'bold', color: getScoreColor(topic.accuracy) }}>
                              {topic.accuracy}%
                            </div>
                            <div style={{ fontSize: '18px', color: '#9ca3af' }}>→</div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
                {weakTopics.length > 10 && (
                  <Link to="/topic-tests" style={{ textDecoration: 'none' }}>
                    <button style={{
                      marginTop: '15px',
                      width: '100%',
                      padding: '12px',
                      background: '#fef2f2',
                      border: '2px solid #fecaca',
                      borderRadius: '8px',
                      color: '#dc2626',
                      fontWeight: '600',
                      fontSize: '14px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#fee2e2';
                      e.currentTarget.style.borderColor = '#ef4444';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#fef2f2';
                      e.currentTarget.style.borderColor = '#fecaca';
                    }}
                    >
                      View All {weakTopics.length} Topics →
                    </button>
                  </Link>
                )}
              </>
            ) : (
              <p style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>
                Complete more tests to see improvement areas
              </p>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div style={{ 
          background: 'white', 
          padding: '30px', 
          borderRadius: '16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937', marginBottom: '20px' }}>
            📅 Recent Activity
          </h2>
          {stats.recentTests.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {stats.recentTests.slice(0, 10).map((test, index) => (
                <div key={index} style={{
                  padding: '20px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                onClick={() => navigate(`/results/${test.resultId || test.id}`)}
                >
                  <div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', marginBottom: '4px' }}>
                      {test.title}
                    </div>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>
                      {test.topic} • {test.date || 'Recently'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ 
                        fontSize: '24px', 
                        fontWeight: 'bold', 
                        color: getScoreColor(test.score) 
                      }}>
                        {test.score}%
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        {test.correctAnswers}/{test.totalQuestions}
                      </div>
                    </div>
                    <div style={{ fontSize: '20px', color: '#9ca3af' }}>→</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: '#6b7280', textAlign: 'center', padding: '40px' }}>
              No tests completed yet. Start practicing to see your activity here!
            </p>
          )}
        </div>

      </div>
    </div>
  );
}

export default Analytics;
